import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageHero from "@/components/feature/PageHero";
import FilmCard from "@/components/feature/FilmCard";
import FilterBar from "./components/FilterBar";
import Pagination from "./components/Pagination";
import usePageMeta from "@/hooks/usePageMeta";
import { CATEGORY_META, FILMS, REGIONS, YEARS } from "@/mocks/films";

const PAGE_SIZE = 6;
type SortKey = "newest" | "alphabet" | "popular" | "rating";

const SORT_LABELS: { key: SortKey; label: string }[] = [
  { key: "newest", label: "发布时间" },
  { key: "popular", label: "热门程度" },
  { key: "rating", label: "评分" },
  { key: "alphabet", label: "字母顺序" },
];

export default function CategoryPage() {
  const { type = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [region, setRegion] = useState(searchParams.get("region") ?? "");
  const [year, setYear] = useState(searchParams.get("year") ?? "");
  const [actor, setActor] = useState(searchParams.get("actor") ?? "");
  const [sort, setSort] = useState<SortKey>("newest");
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  const category = CATEGORY_META.find((item) => item.slug === type);

  useEffect(() => {
    setRegion(searchParams.get("region") ?? "");
    setYear(searchParams.get("year") ?? "");
    setActor(searchParams.get("actor") ?? "");
    setPage(1);
  }, [type, searchParams]);

  usePageMeta({
    title: `${category?.name ?? "分类"} - 欧美成人电影资源分类 - 欧美成人电影`,
    description: category
      ? `欧美成人电影${category.name}类影片资源介绍，${category.desc} 支持按地区、年份与演员标签组合筛选，查看剧情简介、主演与影片详情。`
      : "欧美成人电影分类页，按题材浏览欧美成人影视作品介绍与影片资料。",
    keywords: `欧美成人电影,${category?.name ?? "分类"},欧美影视,成人影视作品,电影介绍`,
    canonicalPath: `/category/${type}`,
  });

  const baseList = useMemo(
    () => FILMS.filter((film) => film.categories.includes(type)),
    [type],
  );

  const filtersActive = Boolean(region || year || actor);

  const filtered = useMemo(() => {
    let list = baseList.filter((film) => {
      if (region && film.region !== region) return false;
      if (year && film.year !== Number(year)) return false;
      if (actor && !film.cast.includes(actor)) return false;
      return true;
    });
    list = [...list].sort((a, b) => {
      switch (sort) {
        case "alphabet":
          return a.titleEn.localeCompare(b.titleEn);
        case "popular":
          return b.views - a.views;
        case "rating":
          return b.rating - a.rating;
        default:
          return b.releaseDate.localeCompare(a.releaseDate);
      }
    });
    return list;
  }, [baseList, region, year, actor, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const actors = useMemo(() => {
    const set = new Set<string>();
    baseList.forEach((film) => film.cast.forEach((name) => set.add(name)));
    return Array.from(set);
  }, [baseList]);

  const resetAll = () => {
    setRegion("");
    setYear("");
    setActor("");
    setSort("newest");
    setPage(1);
    setSearchParams({});
  };

  const changePage = (next: number) => {
    setPage(next);
    window.scrollTo({ top: 320, behavior: "smooth" });
  };

  if (!category) {
    return (
      <SiteLayout>
        <PageHero
          title="分类不存在"
          eyebrow="Category Not Found"
          breadcrumb={[{ label: "首页", to: "/" }, { label: "分类" }]}
          subtitle="该分类可能已调整或尚未开放，你可以从下方入口浏览现有分类。"
        >
          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/"
              className="h-10 px-5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold flex items-center whitespace-nowrap hover:bg-accent-400 transition-colors"
            >
              返回首页
            </Link>
            {CATEGORY_META.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="h-10 px-4 rounded-md border border-background-50/25 text-background-100 text-sm flex items-center whitespace-nowrap hover:bg-background-50/10 transition-colors"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </PageHero>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <PageHero
        eyebrow="Genre Collection"
        title={`${category.name}类欧美成人电影`}
        subtitle={category.desc}
        breadcrumb={[{ label: "首页", to: "/" }, { label: "分类" }, { label: category.name }]}
      >
        <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px] text-background-300">
          <span className="flex items-center gap-1.5">
            <i className="ri-film-line text-accent-400" />
            共 {baseList.length} 部影片资料
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line text-accent-400" />
            最近更新 {baseList[0]?.releaseDate ?? "-"}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line text-accent-400" />
            收录 {actors.length} 位演员
          </span>
        </div>
      </PageHero>

      <div className="w-full px-4 md:px-8 py-8 md:py-12">
        <div className="max-w-[1280px] mx-auto flex flex-col lg:flex-row gap-6 lg:gap-8">
          <FilterBar
            region={region}
            year={year}
            actor={actor}
            regions={REGIONS}
            years={YEARS}
            actors={actors}
            active={filtersActive}
            open={filterOpen}
            onToggle={() => setFilterOpen((v) => !v)}
            onRegionChange={(value) => {
              setRegion(value);
              setPage(1);
            }}
            onYearChange={(value) => {
              setYear(value);
              setPage(1);
            }}
            onActorChange={(value) => {
              setActor(value);
              setPage(1);
            }}
            onReset={resetAll}
          />

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 mb-5 border-b border-background-200">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[12px] text-foreground-500">排序：</span>
                {SORT_LABELS.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      setSort(item.key);
                      setPage(1);
                    }}
                    className={`h-8 px-3 rounded-md text-[12px] whitespace-nowrap transition-colors cursor-pointer ${
                      sort === item.key
                        ? "bg-primary-900 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <p className="text-[12px] text-foreground-500 whitespace-nowrap">
                共 <span className="text-accent-700 font-semibold">{filtered.length}</span> 部 ·
                第 {currentPage}/{totalPages} 页
              </p>
            </div>

            {pageItems.length > 0 ? (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                  {pageItems.map((film) => (
                    <FilmCard key={film.id} film={film} />
                  ))}
                </div>
                <Pagination
                  current={currentPage}
                  total={totalPages}
                  onChange={changePage}
                />
              </>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-100 px-6 py-16 text-center">
                <span className="w-14 h-14 mx-auto rounded-full bg-background-200 text-foreground-400 flex items-center justify-center text-2xl">
                  <i className="ri-inbox-line" />
                </span>
                <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                  当前筛选条件下暂无影片
                </h2>
                <p className="mt-2 text-[13px] text-foreground-500">
                  试着放宽筛选条件，或重置后查看该分类下的全部影片资料。
                </p>
                <button
                  type="button"
                  onClick={resetAll}
                  className="mt-5 inline-flex items-center gap-2 h-10 px-5 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold whitespace-nowrap hover:bg-accent-600 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line" />
                  重置筛选条件
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SiteLayout>
  );
}